"use client";

import { Bell } from "lucide-react";
import Link from "next/link";
import { getUnreadCount } from "@/lib/notifications/storage";
import { useEffect, useState } from "react";

/**
 * NotificationBell component
 * Displays a bell icon with a numeric badge showing unread notification count
 * Badge is hidden when count is 0 (FR-2.6)
 */
export function NotificationBell() {
  const [unreadCount, setUnreadCount] = useState(0);

  // Update count from localStorage on mount and when storage changes
  useEffect(() => {
    const updateCount = () => {
      setUnreadCount(getUnreadCount());
    };

    // Initial load
    updateCount();

    // Listen for storage changes (e.g., from other tabs)
    window.addEventListener("storage", updateCount);

    // Also listen for custom events (for same-tab updates)
    window.addEventListener("vocly:unreadCountChanged", updateCount);

    return () => {
      window.removeEventListener("storage", updateCount);
      window.removeEventListener("vocly:unreadCountChanged", updateCount);
    };
  }, []);

  return (
    <Link
      href="/notifications"
      className="relative inline-flex items-center justify-center p-2 text-[#707090] hover:text-white transition-colors"
      aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
    >
      <Bell className="h-6 w-6" />
      {unreadCount > 0 && (
        <span
          className="absolute -top-1 -right-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-gradient-to-r from-[#ff3ea5] to-[#ff6ac2] px-1.5 text-[10px] font-semibold text-white shadow-[0_0_8px_rgba(255,62,165,0.6)]"
          aria-label={`${unreadCount} unread notifications`}
        >
          {unreadCount > 99 ? "99+" : unreadCount}
        </span>
      )}
    </Link>
  );
}
